<template>
  <Feature
    v-slot="{ feature }"
    :feature-slug="featureNames.COMPANY"
  >
    <div class="mid-gray">
      <Feature :feature-slug="featureNames.ONBOARDING_PREVIEW">
        <div>
          <div v-if="isOnboarding && localData.previewLink">
            <v-alert
              class="mx-n4"
              type="info"
              text
            >
              {{ $t('onboarding.publication.previewMessage') }}
              <a
                :href="localData.previewLink"
                target="_blank"
                class="nav-item text-white"
                rel="noopener noreferrer"
                >{{ $t('onboarding.publication.previewLinkText') }}</a
              >
            </v-alert>
          </div>
        </div>
      </Feature>
      <div>
        <h2 class="pr-4 ml-n2">
          {{ $t('onboarding.publication.contactTitle') }}
        </h2>
      </div>

      <div class="pb-4 mb-2">
        <h4 class="pr-4 ml-n2">
          {{ $t('onboarding.publication.contactSubtitle') }}
        </h4>
      </div>
      <v-row
        class="d-flex"
        :class="{
          'flex-column': $vuetify.display.smAndDown,
          'justify-space-between': !isPlatformTurkoglu,
        }"
      >
        <v-col
          v-if="!isPlatformTurkoglu"
          cols="12"
          md="2"
          class="pr-0 pa-0 pr-md-2"
        >
          <v-text-field
            ref="ambassador.salutation"
            v-model="localData.ambassador.salutation"
            variant="outlined"
            :label="$t('onboarding.publication.salutation')"
            :placeholder="$t('onboarding.publication.salutationPlaceholder')"
            validate-on="blur"
            @update:model-value="updateAmbassadorField('salutation', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          :md="isPlatformTurkoglu ? 6 : 5"
          class="pr-0 pa-0 pr-md-2"
        >
          <v-text-field
            id="ambassador_firstname"
            ref="ambassador.firstname"
            v-model="localData.ambassador.firstname"
            variant="outlined"
            :label="$t('onboarding.publication.firstname')"
            :placeholder="$t('onboarding.publication.firstnamePlaceholder')"
            validate-on="blur"
            :rules="[rules.optionalForOnboarding(isOnboarding)]"
            @update:model-value="updateAmbassadorField('firstname', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          :md="isPlatformTurkoglu ? 6 : 5"
          class="pa-0"
        >
          <v-text-field
            ref="ambassador.lastname"
            v-model="localData.ambassador.lastname"
            variant="outlined"
            :label="$t('onboarding.publication.lastname')"
            :placeholder="$t('onboarding.publication.lastnamePlaceholder')"
            validate-on="blur"
            :rules="[rules.optionalForOnboarding(isOnboarding)]"
            @update:model-value="updateAmbassadorField('lastname', $event)"
          />
        </v-col>
      </v-row>
      <v-row class="d-flex justify-space-between">
        <v-text-field
          ref="ambassador.role"
          v-model="localData.ambassador.role"
          variant="outlined"
          :label="$t('onboarding.publication.role')"
          :placeholder="$t('onboarding.publication.rolePlaceholder')"
          validate-on="blur"
          :rules="[rules.optionalForOnboarding(isOnboarding)]"
          @update:model-value="updateAmbassadorField('role', $event)"
        />
      </v-row>
      <div class="pb-4 mb-2">
        <h4 class="pr-4 ml-n2">
          {{ $t('onboarding.publication.contactDetails') }}
        </h4>
      </div>
      <v-row class="d-flex justify-space-between">
        <v-col
          cols="12"
          sm="6"
          class="pr-0 pa-0 pr-md-2"
        >
          <PhoneField
            id="ambassador_phone"
            v-model="localData.ambassador.phone"
            :rules="[rules.optionalForOnboarding(isOnboarding)]"
            :label="isPlatformTurkoglu ? $t('labels.phone-number-optional') : ''"
            validate-on="blur"
            @update:model-value="updateAmbassadorField('phone', $event)"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
          class="pa-0 mt-7 mt-md-0"
        >
          <PhoneField
            id="ambassador_mobile"
            v-model="localData.ambassador.mobile"
            :label="$t('onboarding.publication.mobile')"
            :rules="[rules.optionalForOnboarding(isOnboarding)]"
            validate-on="blur"
            @update:model-value="updateAmbassadorField('mobile', $event)"
          />
        </v-col>
      </v-row>

      <v-row class="d-flex justify-space-between py-4 mt-7 mt-md-5">
        <v-text-field
          ref="ambassador.email"
          v-model="localData.ambassador.email"
          type="email"
          variant="outlined"
          :label="$t('onboarding.publication.email')"
          :placeholder="$t('onboarding.publication.emailPlaceholder')?.replace('%', '@')"
          :rules="[rules.email, rules.optionalForOnboarding(isOnboarding)]"
          validate-on="blur"
          :disabled="!isOnboarding"
          @update:model-value="updateAmbassadorField('email', $event.toLowerCase())"
        />
      </v-row>
      <div class="pb-4 mb-2">
        <h2 class="pr-4 ml-n2">
          {{ $t('onboarding.publication.sloganTitle') }}
        </h2>
      </div>
      <v-row>
        <v-text-field
          ref="slogan"
          v-model="localData.slogan"
          variant="outlined"
          :label="$t('onboarding.publication.slogan')"
          :placeholder="$t('onboarding.publication.sloganPlaceholder')"
          validate-on="blur"
          @update:model-value="updateField('slogan', $event)"
        />
      </v-row>

      <div v-if="feature.config.isColorEditable">
        <div class="pb-4">
          <h2 class="pr-4 d-inline">
            {{ $t('onboarding.publication.colorsTitle') }}
          </h2>
        </div>
        <div class="pb-4">
          <h4 class="pb-4 d-inline">
            {{ $t('onboarding.publication.colorsSubtitle') }}
          </h4>
        </div>
        <v-row>
          <v-col
            cols="12"
            md="6"
            class="pr-2 pa-0 align-center d-flex flex-column"
          >
            <div class="px-4">
              <h4>
                {{ $t('onboarding.publication.primaryColor') }}
              </h4>
              <ColorPicker
                v-model:value="localData.theme.colors.primary"
                @update:model-value="updateThemeColor('primary', $event)"
              />
            </div>
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="pr-2 pa-0 align-center d-flex flex-column"
          >
            <div class="px-4">
              <h4>
                {{ $t('onboarding.publication.secondaryColor') }}
              </h4>
              <ColorPicker
                v-model:value="localData.theme.colors.secondary"
                @update:model-value="updateThemeColor('secondary', $event)"
              />
            </div>
          </v-col>
        </v-row>
      </div>

      <div class="pt-4 pb-4">
        <h2 class="pr-4 ml-n2">
          {{ $t('onboarding.publication.imagesTitle') }}
        </h2>
      </div>
      <v-row>
        <v-col
          v-for="image of imageArray"
          :key="image.name"
          cols="12"
          md="6"
        >
          <ImageContainer
            v-bind="image"
            :save-image="saveImage"
            :delete-image="deleteImage"
          />
        </v-col>
      </v-row>

      <Feature :feature-slug="featureNames.CONTRACT">
        <div>
          <div
            v-if="isOnboarding"
            class="pt-4"
          >
            <h2 class="pr-4 d-inline">
              {{ $t('onboarding.publication.logoApprovalTitle') }}
            </h2>
          </div>
          <v-row v-if="isOnboarding">
            <v-col>
              <h4 class="pb-4">
                {{ $t('onboarding.publication.logoApprovalSubtitle') }}
              </h4>
              <v-radio-group
                v-model="localData.isReferenceCustomer"
                inline
                :rules="[rules.optionalBooleanForOnboarding(isOnboarding)]"
                @update:model-value="updateField('isReferenceCustomer', $event)"
              >
                <v-radio
                  :label="$t('onboarding.publication.logoApprovalYes')"
                  :value="true"
                />
                <v-radio
                  :label="$t('onboarding.publication.logoApprovalNo')"
                  :value="false"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </div>
      </Feature>
      <Feature :feature-slug="featureNames.MAKLER_MITTEL">
        <template v-if="isOnboarding">
          <v-row
            :class="{
              'integration-partner': true,
              'integration-partner--padding': !isMobile,
            }"
          >
            <v-col
              cols="12"
              md="8"
              class="integration-partner__description"
            >
              <p class="integration-partner__description-title">
                {{ $t('onboarding.publication.widget-integration-title') }}
              </p>
              <p class="mb-2">
                {{ $t('onboarding.publication.widget-integration-subtitle') }}
              </p>
              <p
                class="integration-partner__description-details mb-2"
                v-html="$t('onboarding.publication.widget-integration-description[0]')"
              />
              <p class="integration-partner__description-details">
                {{ $t('onboarding.publication.widget-integration-description[1]') }}
              </p>
              <v-checkbox
                v-model="localData.automaticIntegration"
                @update:model-value="updateField('automaticIntegration', $event)"
                color="white"
                false-icon="mdi-checkbox-blank"
                density="compact"
                :label="$t('onboarding.publication.widget-integration-checkbox')"
                hide-details
                class="mt-2"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
              order="first"
              order-md="2"
              class="integration-partner__image pa-0"
            >
              <v-img src="/img/makler-mitte.png" />
            </v-col>
            <v-col
              cols="12"
              class="pa-0"
              order="3"
            >
              <v-alert
                type="info"
                color="#EEF4FF"
                :text="$t('onboarding.publication.widget-integration-alert')"
              />
            </v-col>
          </v-row>
          <v-row
            class="mt-6"
            v-if="showIntegrationWarning"
          >
            <v-alert type="warning">
              <p class="mb-0">{{ $t('onboarding.publication.widget-integration-reminder[0]') }}</p>
              <p class="mb-0">{{ $t('onboarding.publication.widget-integration-reminder[1]') }}</p>
            </v-alert>
          </v-row>
        </template>
      </Feature>
    </div>
  </Feature>
</template>

<script>
import ImageContainer from '@/components/ImageContainer.vue'
import PhoneField from '@/components/PhoneField.vue'
import heroImage from '@/lib/customHeroMapper'
import { email, optionalBooleanForOnboarding, optionalForOnboarding } from '@/lib/validation'
import brandingMixin from '@/mixins/branding.js'
import featureMixin from '@/mixins/feature'

import ColorPicker from '../ColorPicker.vue'

export default {
  components: { ColorPicker, PhoneField, ImageContainer },
  mixins: [featureMixin, brandingMixin],
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isOnboarding: {
      type: Boolean,
      default: false,
    },
    saveAmbassadorImage: {
      type: Function,
      required: true,
    },
    saveCompanyImage: {
      type: Function,
      required: true,
    },
    deleteCompanyImage: {
      type: Function,
      default: () => {},
    },
    variant: {
      type: String,
      default: '',
    },
    showIntegrationWarning: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:data'],
  data() {
    return {
      localData: {
        ambassador: {
          salutation: this.data.ambassador?.salutation || '',
          firstname: this.data.ambassador?.firstname || '',
          lastname: this.data.ambassador?.lastname || '',
          role: this.data.ambassador?.role || '',
          phone: this.data.ambassador?.phone || '',
          mobile: this.data.ambassador?.mobile || '',
          email: this.data.ambassador?.email || '',
          image: this.data.ambassador?.image || {},
        },
        theme: {
          colors: {
            primary: this.data.theme?.colors?.primary || '',
            secondary: this.data.theme?.colors?.secondary || '',
          },
        },
        slogan: this.data.slogan || '',
        previewLink: this.data.previewLink || '',
        isReferenceCustomer: this.data.isReferenceCustomer || false,
        images: this.data.images || {},
        automaticIntegration: this.data.automaticIntegration || false,
      },
      images: {},
      ambassadorImages: {},
      defaultSignatures: {
        text: 'default text',
        html: '<h3>default html</h3>',
      },
      previewHTMLSignature: false,
      previewTextSignature: false,
      rules: {
        email,
        optionalBooleanForOnboarding,
        optionalForOnboarding,
      },
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.display.smAndDown
    },
    hasLpValuationHero() {
      return this.getFeature(this.featureNames.VALUATION).config.hasLpValuationHero
    },
    valuationHeroImage() {
      return (
        this.localData?.images?.lpValuationHero?.url ||
        heroImage({
          variant: this.variant,
          lpSlug: 'immobilienbewertung',
          useDefault: !this.hasLpValuationHero,
        })
      )
    },
    imageArray() {
      return [
        {
          name: 'logo',
          title: this.$t('onboarding.publication.logo'),
          src: this.images.logo || this.localData.images.logo?.url,
          disabled: !this.getFeature(this.featureNames.COMPANY)?.config?.isLogoEditable,
          minHeight: 100,
          minWidth: 1200,
        },
        {
          name: 'team',
          title: this.$t('onboarding.publication.teamImage'),
          src: this.images.team || this.localData.images.team?.url,
          minHeight: 800,
          minWidth: 1200,
          ratio: 1.5,
        },
        {
          name: 'image',
          title: this.$t('onboarding.publication.portrait'),
          src: this.ambassadorImages.image || this.localData.ambassador.image?.url,
          minHeight: 1200,
          minWidth: 1200,
          ratio: 1,
        },
        {
          name: 'region',
          title: this.$t('onboarding.publication.regionImage'),
          src: this.images.region || this.localData.images.region?.url,
          minHeight: 800,
          minWidth: 1200,
          ratio: 1.5,
          disabled: this.isOnboarding,
        },
        {
          name: 'lpValuationHero',
          title: this.$t('onboarding.publication.valuationHeroImage'),
          src: this.images.lpValuationHero || this.valuationHeroImage,
          minHeight: 650,
          minWidth: 1950,
          ratio: 3,
          disabled: !this.hasLpValuationHero || this.isOnboarding,
          askForImageRights: true,
          canDeleteImage: true,
        },
      ].filter(({ disabled }) => !disabled)
    },
  },
  watch: {
    data: {
      handler(newValue) {
        this.localData = this.initializeLocalData(newValue)
      },
      deep: true,
    },
  },
  methods: {
    initializeLocalData(data) {
      return {
        ambassador: {
          salutation: data.ambassador?.salutation || '',
          firstname: data.ambassador?.firstname || '',
          lastname: data.ambassador?.lastname || '',
          role: data.ambassador?.role || '',
          phone: data.ambassador?.phone || '',
          mobile: data.ambassador?.mobile || '',
          email: data.ambassador?.email || '',
          image: data.ambassador?.image || {},
        },
        theme: {
          colors: {
            primary: data.theme?.colors?.primary || '',
            secondary: data.theme?.colors?.secondary || '',
          },
        },
        slogan: data.slogan || '',
        previewLink: data.previewLink || '',
        isReferenceCustomer: data.isReferenceCustomer || false,
        images: data.images || {},
        automaticIntegration: data.automaticIntegration || false,
      }
    },
    updateAmbassadorField(field, value) {
      const updatedAmbassador = {
        ...this.localData.ambassador,
        [field]: value,
      }
      this.emitUpdate({ ambassador: updatedAmbassador })
    },
    updateThemeColor(colorType, value) {
      const updatedTheme = {
        ...this.localData.theme,
        colors: {
          ...this.localData.theme.colors,
          [colorType]: value,
        },
      }
      this.emitUpdate({ theme: updatedTheme })
    },
    updateField(field, value) {
      this.emitUpdate({ [field]: value })
    },
    emitUpdate(updates) {
      const updatedData = {
        ...this.data,
        ...updates,
      }
      this.$emit('update:data', updatedData)
    },
    saveImage(image, type) {
      if (type === 'image') {
        this.saveImageAmbassador(image, type)
      } else {
        this.images[type] = URL.createObjectURL(image)
        this.saveCompanyImage(image, type)
      }
    },
    deleteImage(type) {
      this.images[type] = null
      this.deleteCompanyImage(type)
    },
    saveImageAmbassador(image, type) {
      this.ambassadorImages[type] = URL.createObjectURL(image)
      this.saveAmbassadorImage(image, type)
    },
  },
}
</script>
<style scoped>
.integration-partner {
  padding: 15px;
  background-color: #1b9185;
  border-radius: 16px;
  color: white;
  margin-top: 32px;
}
.integration-partner--padding {
  padding: 25px 35px;
}
.integration-partner__image {
  display: flex;
  justify-content: center;
  align-items: center;
}
.integration-partner__description-title {
  margin-bottom: 10px;
  font-weight: 700;
  font-size: 26px;
}
.integration-partner__description-details {
  margin-bottom: 0;
}
:deep(
  .integration-partner__description-details a:not(.nav-item):not(.v-alert__content a):not(.v-btn)
) {
  color: #fff !important;
}
:deep(.integration-partner .v-alert__content),
:deep(.integration-partner .v-alert__prepend i) {
  color: #1d7fc9;
}

:deep(.v-selection-control .v-label) {
  font-weight: bold;
  margin-left: 4px;
}
:deep(.v-selection-control__input > .v-icon) {
  opacity: 1;
}
</style>
